import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { User, BookOpen, Utensils, LogOut, Menu, X, Home, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useRecipeStore } from '../store/recipeStore';
import { OnboardingModal } from '../components/OnboardingModal';

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearStore } = useRecipeStore();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    checkUserStores();
  }, []);

  useEffect(() => {
    // Close mobile menu when route changes
    setMobileMenuOpen(false);
  }, [location]);

  const checkUserStores = async () => {
    try {
      const { data: stores, error } = await supabase
        .from('user_grocery_stores')
        .select('id')
        .limit(1);

      if (error) throw error;
      setShowOnboarding(!stores?.length);
    } catch (err) {
      console.error('Error checking user stores:', err);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    clearStore();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {showOnboarding && (
        <OnboardingModal onComplete={() => setShowOnboarding(false)} />
      )}

      {/* Desktop Navigation */}
      <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-20 hidden md:block">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">Recipe Manager</h1>
              <div className="flex items-center gap-4">
                <NavLink
                  to="/feed"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="flex items-center gap-2">
                    <Home size={18} />
                    <span>Feed</span>
                  </div>
                </NavLink>
                <NavLink
                  to="/recipes"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="flex items-center gap-2">
                    <BookOpen size={18} />
                    <span>Recipes</span>
                  </div>
                </NavLink>
                <NavLink
                  to="/meals"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="flex items-center gap-2">
                    <Utensils size={18} />
                    <span>Meals</span>
                  </div>
                </NavLink>
                <NavLink
                  to="/friends"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="flex items-center gap-2">
                    <Users size={18} />
                    <span>Friends</span>
                  </div>
                </NavLink>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <NavLink
                to="/account"
                className={({ isActive }) =>
                  `p-2 rounded-lg transition-colors ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`
                }
              >
                <User size={20} />
              </NavLink>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      {!(window as any).recipeFlutter && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20">
          <div className="grid grid-cols-5 h-16">
            <NavLink
              to="/feed"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center ${isActive
                  ? 'text-blue-600'
                  : 'text-gray-600'
                }`
              }
            >
              <Home size={24} />
              <span className="text-xs mt-1">Feed</span>
            </NavLink>
            <NavLink
              to="/recipes"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center ${isActive
                  ? 'text-blue-600'
                  : 'text-gray-600'
                }`
              }
            >
              <BookOpen size={24} />
              <span className="text-xs mt-1">Recipes</span>
            </NavLink>
            <NavLink
              to="/meals"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center ${isActive
                  ? 'text-blue-600'
                  : 'text-gray-600'
                }`
              }
            >
              <Utensils size={24} />
              <span className="text-xs mt-1">Meals</span>
            </NavLink>
            <NavLink
              to="/friends"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center ${isActive
                  ? 'text-blue-600'
                  : 'text-gray-600'
                }`
              }
            >
              <Users size={24} />
              <span className="text-xs mt-1">Friends</span>
            </NavLink>
            <NavLink
              to="/account"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center ${isActive
                  ? 'text-blue-600'
                  : 'text-gray-600'
                }`
              }
            >
              <User size={24} />
              <span className="text-xs mt-1">Account</span>
            </NavLink>
          </div>
        </nav>
      )}


      <main className="max-w-4xl mx-auto px-4 py-6 pt-5 pb-24 md:pb-6">
        <Outlet />
      </main>
    </div>
  );
}